import https from "@/lib/https";
import type { ResumeTemplate, TemplatePreview } from "@/interface/template";
import type { ApiResponse } from "@/interface";

const BASE_URL = "/api/master/resume-templates/";

export interface ExportProfilePayload {
  template: string;
  exclude?: Record<string, any>;
}

export interface ExportBulkPayload {
  template: string;
  exclude?: Record<string, any>;
  profile_ids: (string | number)[];
  format: "pdf" | "docx";
}

export interface ExportPreviewData {
  template_slug: string;
  template_name: string;
  html: string;
}

export interface ExportPreviewResponse {
  success: boolean;
  message?: string;
  data: ExportPreviewData;
  status?: number;
}

export const getResumeTemplates = async (): Promise<ResumeTemplate[]> => {
  const response = await https.get<
    ResumeTemplate[] | { data: ResumeTemplate[] }
  >(BASE_URL);
  if (Array.isArray(response)) {
    return response;
  }
  if (response && Array.isArray((response as any).data)) {
    return (response as any).data;
  }
  return [];
};

export const getAllTemplatesPreview = async (
  profileId: string | number,
): Promise<ApiResponse<TemplatePreview[]>> => {
  return await https.get<ApiResponse<TemplatePreview[]>>(
    `/api/profiles/${profileId}/all-templates-preview/`,
  );
};

export const getExportPreview = async (
  id: string | number,
  payload: ExportProfilePayload,
): Promise<ExportPreviewResponse> => {
  return await https.post<ExportPreviewResponse>(
    `/api/profiles/${id}/export-preview/`,
    payload,
    { showToast: false },
  );
};

/**
 * Triggers direct browser download for a Blob response.
 */
const triggerDownload = (blob: Blob, filename: string) => {
  if (!blob) return;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const exportProfilePdf = async (
  id: string | number,
  payload: ExportProfilePayload,
  filename?: string,
) => {
  const blob = await https.post<Blob>(
    `/api/profiles/${id}/export-pdf/`,
    payload,
    { responseType: "blob" },
  );
  triggerDownload(blob, `${filename || `candidate_${id}_resume`}.pdf`);
  return blob;
};

export const exportProfileDocx = async (
  id: string | number,
  payload: ExportProfilePayload,
  filename?: string,
) => {
  const blob = await https.post<Blob>(
    `/api/profiles/${id}/export-docx/`,
    payload,
    { responseType: "blob" },
  );
  triggerDownload(blob, `${filename || `candidate_${id}_resume`}.docx`);
  return blob;
};

export const exportBulkPdf = async (payload: ExportBulkPayload) => {
  const blob = await https.post<Blob>(
    `/api/profiles/export-bulk-pdf/`,
    payload,
    { responseType: "blob" },
  );
  triggerDownload(blob, `bulk_resumes.zip`);
  return blob;
};

export const exportBulkDocx = async (payload: ExportBulkPayload) => {
  const blob = await https.post<Blob>(
    `/api/profiles/export-bulk-docx/`,
    payload,
    { responseType: "blob" },
  );
  triggerDownload(blob, `bulk_resumes.zip`);
  return blob;
};
